import React from 'react';
/**
 * Feedback Section Builder — Constructs accordion sections for the detail phase.
 *
 * Extracted from FeedbackDisplay.tsx (691→~300 lines) during CL-1 prep s62.
 * Co-located in practice/ domain (CL-1 s64).
 */
import type { Question } from '../../../content';
import { DjayAvatar, type DjayVariant } from '../../../mascot';
import { VisualAidFactory, shouldShowVisualAid } from '../../../visuals';
import type { QuestionResolvedSupport } from '../../../support';
import { ExplanationContent } from './ExplanationContent';
import type { AccordionSection } from './FeedbackAccordion';
import { type MoneyToken } from './feedbackAnswerFormatters';
import { formatMoneyDisplay } from '../../../utils/moneyFormatters';
import { resolveCityName, distanceKm } from '../../../visuals/components/topoMap/nameResolver';
import { StepSolverConfig } from '../../../content/types';
// ── Types ────────────────────────────────────────────────────
// S-1 (Support RC audit): renamed from non-existent `SupportContent` to the
// actually-exported `QuestionResolvedSupport` from src/support/index.ts. The
// shape is identical to what was being passed in — this is purely a type
// import fix.
interface SectionBuilderInput {
  question: Question;
  wasCorrect: boolean;
  resolvedSupport: QuestionResolvedSupport;
  distractorExplanation?: string;
  selectedVariant: DjayVariant;
  levelUp?: {
    level: number;
    xp: number;
    title: string;
  } | null;
  userMoneyTokens: MoneyToken[] | null;
  correctCents: number | null;
  userAnswer?: string;
}
// ── Helpers ──────────────────────────────────────────────────
function getVisualHeaderText(resolvedSupport: QuestionResolvedSupport, isMoneyAssembler: boolean): string {
  if (isMoneyAssembler) return 'Vergelijk de munten';
  if (!resolvedSupport.visual) return 'Kijk eens!';
  switch (resolvedSupport.visual.type) {
    case 'fraction':
      return 'Kijk, zo zie je het!';
    case 'geo':
      return 'Kijk, zo meet je het!';
    case 'rule':
      return 'Onthoud deze regel!';
    case 'column':
      return 'Stap voor stap!';
    default:
      return 'Kijk eens!';
  }
}
function getMoneyStyles(cents: number): string {
  if (cents >= 500) return 'w-14 h-9 rounded-lg bg-slate-500 text-white font-bold text-xs shadow-sm';
  if (cents >= 100) return 'w-9 h-9 rounded-full bg-amber-400 text-amber-900 font-bold text-xs shadow-sm';
  if (cents >= 50) return 'w-9 h-9 rounded-full bg-yellow-400 text-yellow-900 font-bold text-[10px] shadow-sm';
  return 'w-7 h-7 rounded-full bg-orange-500 text-white font-bold text-[9px] shadow-sm';
}
function getDenominationDisplay(cents: number): string {
  return cents >= 100 ? `€${cents / 100}` : `${cents}c`;
}
function MoneyTokenGrid({
  tokens,
  wasCorrect,
  correctCents
}: {
  tokens: MoneyToken[];
  wasCorrect: boolean;
  correctCents: number;
}) {
  return <div className="space-y-4" data-id="element-9776">
      <div data-id="element-9777">
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-9778">
          {wasCorrect ? '✅ Jouw antwoord' : '❌ Jouw antwoord'}
        </h4>
        <div className="bg-white rounded-xl p-3 border border-slate-200" data-id="element-9779">
          <div className="flex flex-wrap gap-2" data-id="element-9780">
            {tokens.map((token, idx) => <div key={idx} className="flex items-center gap-1" data-id="element-9781">
                <div className={`flex items-center justify-center ${getMoneyStyles(token.valueCents)}`} data-id="element-9782">
                  <span data-id="element-9783">{getDenominationDisplay(token.valueCents)}</span>
                </div>
                {token.count > 1 && <span className="text-xs font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded-md" data-id="element-9784">
                    ×{token.count}
                  </span>}
              </div>)}
          </div>
        </div>
      </div>
      {!wasCorrect && <div data-id="element-9785">
          <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2" data-id="element-9786">
            ✅ Correct antwoord
          </h4>
          <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200" data-id="element-9787">
            <p className="text-lg font-black text-emerald-700" data-id="element-9788">
              {formatMoneyDisplay(correctCents)}
            </p>
          </div>
        </div>}
    </div>;
}
// ── Map Feedback Helpers ─────────────────────────────────────
interface MapTargetPoint {
  cityId?: string;
  name?: string;
  lat: number;
  lon: number;
  label?: string;
}
interface MapPlacement {
  lat: number;
  lon: number;
}
function MapPlaceCitiesBreakdown({
  question,
  userAnswer
}: {
  question: Question;
  userAnswer: string;
}) {
  const data = (question.data ?? {}) as Record<string, unknown>;
  const targets = data.targetPoints as MapTargetPoint[] | undefined ?? [];
  const tolKm = typeof data.toleranceKm === 'number' ? data.toleranceKm : 35;
  let placements: (MapPlacement | null)[] = [];
  try {
    const parsed = JSON.parse(userAnswer);
    if (Array.isArray(parsed)) placements = parsed;
  } catch {
    /* empty */
  }
  const rows = targets.map((t, i) => {
    const name = t.name ?? resolveCityName(t.cityId) ?? `Stad ${i + 1}`;
    const p = placements[i];
    if (!p || typeof p.lat !== 'number' || typeof p.lon !== 'number') {
      return {
        name,
        ok: false,
        missing: true,
        distance: 0
      };
    }
    const dist = distanceKm(p, {
      lat: t.lat,
      lon: t.lon
    });
    return {
      name,
      ok: dist <= tolKm,
      missing: false,
      distance: dist
    };
  });
  const okCount = rows.filter(r => r.ok).length;
  const total = rows.length;
  return <div className="space-y-3" data-id="element-9789">
      <div className="text-sm font-bold text-slate-700" data-id="element-9790">
        {okCount} van de {total} goed geplaatst
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-9791">
        {rows.map((r, i) => <div key={i} className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 border ${r.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-id="element-9792">
            <div className="flex items-center gap-2 min-w-0" data-id="element-9793">
              <span className="text-base" data-id="element-9794">{r.ok ? '✅' : '❌'}</span>
              <span className={`font-bold text-sm truncate ${r.ok ? 'text-emerald-900' : 'text-rose-900'}`} data-id="element-9795">
                {r.name}
              </span>
            </div>
            {!r.ok && <span className="text-[11px] font-bold text-rose-600 whitespace-nowrap" data-id="element-9796">
                {r.missing ? 'niet geplaatst' : `${Math.round(r.distance)} km mis`}
              </span>}
          </div>)}
      </div>
    </div>;
}
function MapLocatePointBreakdown({
  question,
  userAnswer
}: {
  question: Question;
  userAnswer: string;
}) {
  const data = (question.data ?? {}) as Record<string, unknown>;
  const target = data.targetPoint as MapTargetPoint | undefined;
  if (!target) return null;
  let click: MapPlacement | null = null;
  try {
    const parsed = JSON.parse(userAnswer);
    if (parsed && typeof parsed.lat === 'number' && typeof parsed.lon === 'number') {
      click = {
        lat: parsed.lat,
        lon: parsed.lon
      };
    }
  } catch {
    /* empty */
  }
  if (!click) {
    return <div className="text-sm text-slate-700" data-id="element-9797">
        Je hebt geen pin op de kaart geplaatst.
      </div>;
  }
  const dist = distanceKm(click, {
    lat: target.lat,
    lon: target.lon
  });
  const targetName = target.label ?? target.name ?? (target.cityId ? resolveCityName(target.cityId) : 'de juiste plek');
  return <div className="space-y-2" data-id="element-9798">
      <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 bg-rose-50 border border-rose-200" data-id="element-9799">
        <span className="text-base" data-id="element-9800">📍</span>
        <span className="font-bold text-sm text-rose-900" data-id="element-9801">
          Je pin lag <strong data-id="element-9802">{Math.round(dist)} km</strong> van {targetName}.
        </span>
      </div>
    </div>;
}
function StepSolverBreakdown({
  question,
  userAnswer
}: {
  question: Question;
  userAnswer: string;
}) {
  const config = (question.data?.stepSolver || question.data || {}) as StepSolverConfig;
  const steps = config.steps || [];
  let parsedAnswers: Record<number, string> = {};
  try {
    parsedAnswers = JSON.parse(userAnswer);
  } catch {
    /* empty */
  }
  return <div className="space-y-3" data-id="element-9803">
      {steps.map((step, idx) => {
      const userVal = parsedAnswers[idx]?.trim() || '';
      const accepted = step.acceptedAnswers || [step.correctAnswer];
      const isCorrect = accepted.some(ans => {
        const acceptedStr = String(ans).trim();
        if (acceptedStr.toLowerCase() === userVal.toLowerCase()) return true;
        const userNum = parseFloat(userVal.replace(/[€\s,]/g, '.'));
        const acceptedNum = parseFloat(acceptedStr.replace(/[€\s,]/g, '.'));
        if (!isNaN(userNum) && !isNaN(acceptedNum)) {
          return Math.abs(userNum - acceptedNum) < 0.001; // NUMERIC_TOLERANCE
        }
        return false;
      });
      return <div key={idx} className={`p-3 rounded-xl border ${isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-id="element-9804">
            <div className="flex items-start gap-3" data-id="element-9805">
              <div className="mt-0.5 text-lg" data-id="element-9806">{isCorrect ? '✅' : '❌'}</div>
              <div className="flex-1 min-w-0" data-id="element-9807">
                <p className="font-bold text-sm text-slate-800 mb-1" data-id="element-9808">
                  {step.prompt}
                </p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-id="element-9809">
                  <span className="text-slate-600" data-id="element-9810">
                    Jouw antwoord:{' '}
                    <strong className={isCorrect ? 'text-emerald-700' : 'text-rose-700'} data-id="element-9811">
                      {userVal || '-'}
                    </strong>
                  </span>
                  {!isCorrect && <span className="text-slate-600" data-id="element-9812">
                      Correct:{' '}
                      <strong className="text-emerald-700" data-id="element-9813">
                        {step.correctAnswer}
                      </strong>
                    </span>}
                </div>
                {!isCorrect && (step.explanation || step.hint) && <div className="mt-2 text-sm text-slate-600 italic bg-white/50 p-2 rounded-lg" data-id="element-9814">
                    💡 {step.explanation || step.hint}
                  </div>}
              </div>
            </div>
          </div>;
    })}
    </div>;
}
// ── Main Builder ─────────────────────────────────────────────
export function buildAccordionSections(input: SectionBuilderInput): AccordionSection[] {
  const {
    question,
    wasCorrect,
    resolvedSupport,
    distractorExplanation,
    selectedVariant,
    levelUp,
    userMoneyTokens,
    correctCents,
    userAnswer
  } = input;
  const isMoneyAssembler = question.questionType === 'money';
  const supportVisuals = question.supportAids || [];
  const showSupportVisuals = supportVisuals.some(aid => shouldShowVisualAid(aid, wasCorrect));
  const sections: AccordionSection[] = [];
  // 0. Level Up Info
  if (levelUp && wasCorrect) {
    sections.push({
      id: 'levelup',
      icon: <span className="text-lg" data-id="element-9815">🌟</span>,
      title: `Level ${levelUp.level} — ${levelUp.title}`,
      subtitle: `Je hebt nu ${levelUp.xp} XP!`,
      content: <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center" data-id="element-9816">
          <p className="text-amber-800 font-bold text-lg mb-1" data-id="element-9817">
            Gefeliciteerd met je nieuwe level! 🎉
          </p>
          <p className="text-amber-700 font-medium text-sm" data-id="element-9818">
            Blijf oefenen om nog verder te groeien.
          </p>
        </div>
    });
  }
  // 1. Explanation (only for correct answers — wrong answers show explanation directly)
  if (wasCorrect && resolvedSupport.explanation) {
    sections.push({
      id: 'explanation',
      icon: <DjayAvatar size="sm" emotion="happy" variant={selectedVariant} animate={false} data-id="element-9819" />,
      title: 'Waarom dit klopt',
      content: <ExplanationContent explanation={resolvedSupport.explanation || ''} distractorExplanation={undefined} isCorrect={wasCorrect} solutionSteps={resolvedSupport.solutionSteps} data-id="element-9820" />
    });
  }
  // 1.5 Map question per-city / per-pin breakdown
  if (question.questionType === 'map_place_cities') {
    sections.push({
      id: 'map_breakdown',
      icon: <span className="text-lg" data-id="element-9821">🗺️</span>,
      title: wasCorrect ? 'Jouw plaatsingen' : 'Per stad bekeken',
      content: <MapPlaceCitiesBreakdown question={question} userAnswer={userAnswer ?? ''} data-id="element-9822" />
    });
  }
  if (question.questionType === 'map_locate_point' && !wasCorrect) {
    sections.push({
      id: 'map_distance',
      icon: <span className="text-lg" data-id="element-9823">📍</span>,
      title: 'Hoe ver zat je ernaast?',
      content: <MapLocatePointBreakdown question={question} userAnswer={userAnswer ?? ''} data-id="element-9824" />
    });
  }
  // 1.6 Step Solver Breakdown
  if (question.questionType === 'step_solver') {
    sections.push({
      id: 'step_solver_breakdown',
      icon: <span className="text-lg" data-id="element-9825">📝</span>,
      title: 'Stap voor stap bekeken',
      content: <StepSolverBreakdown question={question} userAnswer={userAnswer ?? ''} data-id="element-9826" />
    });
  }
  // 2. Visual Aids
  if (!isMoneyAssembler && showSupportVisuals) {
    sections.push({
      id: 'visual',
      icon: <DjayAvatar size="sm" emotion="pointing" variant={selectedVariant} animate={false} data-id="element-9827" />,
      title: getVisualHeaderText(resolvedSupport, isMoneyAssembler),
      content: <div className="space-y-4" data-id="element-9828">
          {supportVisuals.filter(aid => shouldShowVisualAid(aid, wasCorrect)).map((aid, index) => <VisualAidFactory key={index} visualAid={aid} isCorrect={wasCorrect} mode="support" data-id="element-9829" />)}
        </div>
    });
  }
  // 3. Money Assembler Feedback
  if (isMoneyAssembler && userMoneyTokens && correctCents !== null) {
    sections.push({
      id: 'money',
      icon: <span className="text-lg" data-id="element-9830">🪙</span>,
      title: wasCorrect ? 'Jouw munten' : 'Vergelijk de munten',
      content: <MoneyTokenGrid tokens={userMoneyTokens} wasCorrect={wasCorrect} correctCents={correctCents} data-id="element-9831" />
    });
  }
  return sections;
}